
import React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { JournalEntry } from "@/hooks/journal/types";
import JournalEntryCardHeader from "./JournalEntryCardHeader";
import JournalEntryCardActions from "./JournalEntryCardActions";
import JournalEntryCardContent from "./JournalEntryCardContent";

interface JournalEntryCardProps {
  entry: JournalEntry;
  onView: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  isDeleting: boolean;
  entryToDelete: string | null;
}

const JournalEntryCard: React.FC<JournalEntryCardProps> = ({
  entry,
  onView,
  onDelete,
  isDeleting,
  entryToDelete
}) => {
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  const isComplete = entry.progress >= (entry.progress_max || 7);

  return (
    <Card className={cn(
      "w-full shadow-md backdrop-blur-sm overflow-hidden relative",
      // RPG mode styling
      enableRpgMode ? (
        rpgTheme.cardBackground + " border-2 border-amber-600/50 shadow-lg"
      ) : (
        // Base styling with proper conditional logic
        [
          darkMode 
            ? "bg-[#2d1a45]/80 text-[#FBF3F1]" 
            : "bg-white/90",
          // Border styling based on completion status
          isComplete
            ? "border-2 border-green-500"
            : darkMode 
              ? "border border-[#6127A0]/30" 
              : "border border-[#D896A4]/10"
        ].join(" ")
      )
    )}>
      {enableRpgMode && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/10 to-amber-900/20 rounded-lg pointer-events-none" />
      )}
      
      <div className="p-4 relative z-10">
        <div className="flex justify-between items-center mb-3">
          <JournalEntryCardHeader
            diceValue={entry.dice_value}
            createdAt={entry.created_at}
            isComplete={isComplete}
          />
          <JournalEntryCardActions
            entryId={entry.id}
            entry={entry}
            onView={onView}
            onDelete={onDelete}
            isDeleting={isDeleting}
            entryToDelete={entryToDelete}
          />
        </div>
        
        <JournalEntryCardContent
          stuckPoint={entry.stuck_point}
          nextStepResponse={entry.next_step_response}
          progress={entry.progress}
          progressMax={entry.progress_max}
        />
      </div>
    </Card>
  );
};

export default JournalEntryCard;
